import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
} from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-final-form';
import { Button, Modal } from 'ncoded-component-library';
import {
  ModalProps,
  ModalRef,
} from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import useCreateSelectOptions from 'hooks/useCreateSelectOptions';
import RadioGroupField from 'components/RadioGroupField';
import DownloadIcon from 'icons/Download.icon';
import {
  ACTIVITY_LOG_EXPORT_FORMAT,
  ActivityLogExportFormat,
} from '../../pages/Reports/types';

import './ExportActivityModal.styles.scss';

type FormValues = {
  format: ActivityLogExportFormat;
};

type ExportActivityModalProps = {
  className?: string;
  onExport: (format: ActivityLogExportFormat) => Promise<void> | void;
} & ModalProps;

const ExportActivityModal: React.ForwardRefRenderFunction<
  ModalRef,
  ExportActivityModalProps
> = (props, ref) => {
  const { className, onExport, ...rest } = props;

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const classes = classNames('export-activity-modal', className);

  const { t } = useTranslation();

  const formatOptions = useCreateSelectOptions()(
    Object.values(ACTIVITY_LOG_EXPORT_FORMAT),
  );

  const initialValues = useMemo<FormValues>(
    () => ({ format: ACTIVITY_LOG_EXPORT_FORMAT.PDF }),
    [],
  );

  const onCancel = useCallback(() => modal?.close(), [modal]);

  const onSubmit = useCallback(
    async ({ format }: FormValues) => {
      await onExport(format);

      onCancel();
    },
    [onExport, onCancel],
  );

  return (
    <Modal
      ref={modalRef}
      className={classes}
      title={t('exportActivity')}
      {...rest}
    >
      <p className="description">{t('exportsTheRowsCurrentlyShown')}</p>

      <Form
        initialValues={initialValues}
        onSubmit={onSubmit}
        render={({ handleSubmit, submitting }) => (
          <form onSubmit={handleSubmit} className="export-activity-form">
            <RadioGroupField
              required
              name="format"
              label={t('chooseFileType')}
              options={formatOptions}
            />

            <div className="actions">
              <Button variant="link" disabled={submitting} onClick={onCancel}>
                {t('cancel')}
              </Button>

              <Button type="submit" icon={DownloadIcon} disabled={submitting}>
                {t('export')}
              </Button>
            </div>
          </form>
        )}
      />
    </Modal>
  );
};

export default forwardRef(ExportActivityModal);
